import { useStore } from '@nanostores/react';
import { memo, useEffect } from 'react';
import type { Stripe } from 'stripe';
import { DialogContentSelectProducts } from './DialogContentSelectProducts';
import { DialogContentSetApiKey } from './DialogContentSetApiKey';
import { Dialog } from '~/components/ui/LegacyDialog';
import { checkStripeSecretKeyAndListProducts } from '~/lib/.client/api/stripe';
import { projectStore } from '~/lib/stores/project';
import { stripeStore } from '~/lib/stores/stripe';
import { invariant } from '~/utils/invariant';

interface StripeConfigurationDialogProps {
  open: boolean;
  checkSecretThenGetProducts?: (projectId: string, secretKey: string) => Promise<Array<Stripe.Product>>;
}

function onClose() {
  stripeStore.isModalOpened.set(false);
}

export const StripeConfigurationDialog = memo<StripeConfigurationDialogProps>(
  ({ open, checkSecretThenGetProducts = checkStripeSecretKeyAndListProducts }) => {
    const products = useStore(stripeStore.products);
    const tab = useStore(stripeStore.modalTab);

    useEffect(() => {
      if (open && tab === 'edit-product') {
        const showProductSelection = stripeStore.partialKey.get() !== '';

        if (showProductSelection) {
          const projectId = projectStore.projectId.get();

          invariant(projectId);

          stripeStore.refreshProducts(projectId, { onlyIfEmpty: true });
        }
      }
    }, [open, tab]);

    return (
      <Dialog
        showCloseButton={tab !== 'edit-product'}
        showOverflow
        open={open}
        onClose={onClose}
        maxWidth={tab === 'edit-product' ? 672 : 550}
        maxHeight={tab === 'edit-product' ? 700 : 550}
      >
        {tab === 'api-key' && (
          <DialogContentSetApiKey
            checkSecretThenGetProducts={checkSecretThenGetProducts}
            onClose={onClose}
            setProducts={(products) => {
              stripeStore.modalTab.set('edit-product');
              stripeStore.setProducts(products);
            }}
          />
        )}
        {tab === 'edit-product' && <DialogContentSelectProducts onClose={onClose} products={products} />}
      </Dialog>
    );
  },
);
